/**
 * HR-portal FTUE — shared flow contract.
 *
 * One linear coachmark tour (`portal-hr-activation`) walks a first-time HR admin
 * through portal setup: dashboard orientation, then a sidebar-item -> on-page
 * pair for each area (HR System, People/Import, Stakeholders, Severance, Exit
 * Guide, Internal Mobility), then a wrap-up.
 *
 * The tour reuses the job-seeker FTUE engine (`src/ftue`) unchanged. The engine's
 * `completedSteps` id space is shared across every Pelgo site, so every id here
 * is prefixed `portal-hr-` and every anchor id `portal-` to avoid collisions.
 *
 * This module is the single source the per-page hooks import from: the flow id,
 * the step ids, the anchor ids, the global step order, and the `portalStep`
 * factory that stamps every step with the shared portal chrome. `use-portal-hr-ftue`
 * owns the dashboard + sidebar-nav steps and the start logic; each
 * `use-portal-<area>-ftue` hook owns one on-page step. Splitting the definitions
 * across hooks (rather than one big registry call) lets a step's `condition`
 * close over live page state, matching the job-seeker pattern
 * (`use-opportunities-ftue`, `use-learn-ftue`).
 */

import { type FtueStep, useFtueStore } from '@/ftue'

/** The single flow id for the whole HR-portal tour. */
export const PORTAL_HR_ACTIVATION_FLOW = 'portal-hr-activation'

/**
 * Page-owned hooks may ask an already-running HR tour to re-evaluate after
 * registering steps or changing page state. They must never start the tour or
 * replace another active FTUE flow; PortalLayout exclusively owns activation.
 */
export function reevaluatePortalHrFlow(): void {
  const store = useFtueStore.getState()
  if (store.activeFlow === PORTAL_HR_ACTIVATION_FLOW) {
    store.startFlow(PORTAL_HR_ACTIVATION_FLOW)
  }
}

/** Step ids. Also used verbatim as each step's `phase` so every coachmark renders
 *  single-concept (no "N of M" chrome), matching the resume-builder journey. */
export const PORTAL_HR_STEP = {
  welcome: 'portal-hr-welcome',
  dashboardStats: 'portal-hr-dashboard-stats',
  dashboardImportCta: 'portal-hr-dashboard-import-cta',
  dashboardRecentImports: 'portal-hr-dashboard-recent-imports',
  navHris: 'portal-hr-nav-hris',
  hrisConnect: 'portal-hr-hris-connect',
  hrisWizardFinish: 'portal-hr-hris-wizard-finish',
  hrisConnectedSync: 'portal-hr-hris-connected-sync',
  navImport: 'portal-hr-nav-import',
  importDropzone: 'portal-hr-import-dropzone',
  importTemplate: 'portal-hr-import-template',
  importReview: 'portal-hr-import-review',
  importConfirm: 'portal-hr-import-confirm',
  navPeople: 'portal-hr-nav-people',
  peopleEmpty: 'portal-hr-people-empty',
  peopleFilters: 'portal-hr-people-filters',
  peopleTable: 'portal-hr-people-table',
  navStakeholders: 'portal-hr-nav-stakeholders',
  stakeholdersAdd: 'portal-hr-stakeholders-add',
  stakeholdersEditRow: 'portal-hr-stakeholders-edit-row',
  stakeholdersHistory: 'portal-hr-stakeholders-history',
  navSeverance: 'portal-hr-nav-severance',
  severanceUpload: 'portal-hr-severance-upload',
  severanceTemplate: 'portal-hr-severance-template',
  severanceReview: 'portal-hr-severance-review',
  severanceConfirm: 'portal-hr-severance-confirm',
  navSeveranceElections: 'portal-hr-nav-severance-elections',
  severanceCohortSettings: 'portal-hr-severance-cohort-settings',
  severanceDownloadCsv: 'portal-hr-severance-download-csv',
  severanceTable: 'portal-hr-severance-table',
  navExitGuide: 'portal-hr-nav-exit-guide',
  exitGuideUpload: 'portal-hr-exit-guide-upload',
  exitGuideLibraryIntro: 'portal-hr-exit-guide-library-intro',
  exitGuideRowActions: 'portal-hr-exit-guide-row-actions',
  navInternalJobs: 'portal-hr-nav-internal-jobs',
  internalJobsAdd: 'portal-hr-internal-jobs-add',
  internalJobsImport: 'portal-hr-internal-jobs-import',
  internalJobsFilters: 'portal-hr-internal-jobs-filters',
  internalJobsRowActions: 'portal-hr-internal-jobs-row-actions',
  wrapUp: 'portal-hr-wrap-up',
} as const

/** Every step id, for the "has this admin seen any of the tour?" start check. */
export const PORTAL_HR_STEP_IDS: readonly string[] = Object.values(PORTAL_HR_STEP)

/** Anchor-registry keys. Attached with `useFtueAnchor(id)` on the target element. */
export const PORTAL_HR_ANCHOR = {
  // dashboard (use-portal-hr-ftue / use-portal-dashboard-extras-ftue)
  dashboardHero: 'portal-dashboard-hero',
  dashboardStats: 'portal-dashboard-stats',
  dashboardImportCta: 'portal-dashboard-import-cta',
  dashboardRecentImports: 'portal-dashboard-recent-imports',
  // sidebar nav links (portal-layout, registered by use-portal-hr-ftue's anchors)
  navHris: 'portal-nav-hris',
  navImport: 'portal-nav-import',
  navPeople: 'portal-nav-people',
  navStakeholders: 'portal-nav-stakeholders',
  navSeveranceImport: 'portal-nav-severance-import',
  navSeveranceElections: 'portal-nav-severance-elections',
  navExitGuide: 'portal-nav-exit-guide',
  navInternalJobs: 'portal-nav-internal-jobs',
  // on-page targets (one per use-portal-<area>-ftue hook)
  hrisConnectCta: 'portal-hris-connect-cta',
  hrisWizardFinishCta: 'portal-hris-wizard-finish-cta',
  hrisConnectedActions: 'portal-hris-connected-actions',
  importDropzone: 'portal-import-dropzone',
  importTemplateCta: 'portal-import-template-cta',
  importReviewTable: 'portal-import-review-table',
  importConfirmCta: 'portal-import-confirm-cta',
  peopleEmpty: 'portal-people-empty',
  peopleFilters: 'portal-people-filters',
  peopleTable: 'portal-people-table',
  stakeholderAddContact: 'portal-stakeholder-add-contact',
  stakeholderContactRow: 'portal-stakeholder-contact-row',
  stakeholderHistoryCta: 'portal-stakeholder-history-cta',
  severanceImportDropzone: 'portal-severance-import-dropzone',
  severanceTemplateCta: 'portal-severance-template-cta',
  severanceReviewTable: 'portal-severance-review-table',
  severanceConfirmCta: 'portal-severance-confirm-cta',
  severanceCohortSettingsCta: 'portal-severance-cohort-settings-cta',
  severanceDownloadCsvCta: 'portal-severance-download-csv-cta',
  severanceElectionsTable: 'portal-severance-elections-table',
  exitGuideUpload: 'portal-exitguide-upload',
  exitGuideLibrary: 'portal-exitguide-library',
  exitGuideRowActions: 'portal-exitguide-row-actions',
  jobsAddCta: 'portal-jobs-add-cta',
  jobsImportCta: 'portal-jobs-import-cta',
  jobsFilters: 'portal-jobs-filters',
  jobsRowActions: 'portal-jobs-row-actions',
} as const

/**
 * Global step order. Centralised so the six page hooks (authored in parallel)
 * never collide. Gaps between the tens leave room for the nav step (x0) and its
 * on-page follow-up (x2) of each area.
 */
export const PORTAL_HR_ORDER = {
  welcome: 1,
  dashboardStats: 2,
  dashboardImportCta: 3,
  dashboardRecentImports: 4,
  navHris: 10,
  hrisConnect: 12,
  hrisWizardFinish: 13,
  hrisConnectedSync: 14,
  navImport: 20,
  importDropzone: 22,
  importTemplate: 23,
  importReview: 24,
  importConfirm: 25,
  navPeople: 26,
  peopleEmpty: 27,
  peopleFilters: 28,
  peopleTable: 29,
  navStakeholders: 30,
  stakeholdersAdd: 32,
  stakeholdersEditRow: 33,
  stakeholdersHistory: 34,
  navSeverance: 40,
  severanceUpload: 42,
  severanceTemplate: 43,
  severanceReview: 44,
  severanceConfirm: 45,
  navSeveranceElections: 46,
  severanceCohortSettings: 47,
  severanceDownloadCsv: 48,
  severanceTable: 49,
  navExitGuide: 50,
  exitGuideUpload: 52,
  exitGuideLibraryIntro: 53,
  exitGuideRowActions: 54,
  navInternalJobs: 60,
  internalJobsAdd: 62,
  internalJobsImport: 63,
  internalJobsFilters: 64,
  internalJobsRowActions: 65,
  wrapUp: 99,
} as const

/**
 * Shared `phase` strings for step groups that ARE a multi-step sequence within
 * one branch (chromes as "N of M", matching the Learn tour's empty-branch
 * shape) -- distinct from `portalStep()`'s default of one phase per step.
 * `skipScope: 'phase'` is already the default on every step, so setting a
 * shared phase is the only override a grouped step needs.
 */
export const PORTAL_HR_PHASE = {
  hrisWizard: 'portal-hr-hris-wizard',
  peopleReady: 'portal-hr-people-ready',
  exitGuideLibrary: 'portal-hr-exit-guide-library',
} as const

/** The portal sidebar is `hidden md:flex`; below md it lives in a closed drawer,
 *  so nav-anchored steps must gate on this (their anchor is otherwise absent).
 *  Treats a missing `matchMedia` (non-browser / test env) as "not desktop". */
export const isPortalDesktop = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(min-width: 768px)').matches

/** True while the browser is on `path` (or a subpath). Portal steps gate on this
 *  so an immediate step whose anchor is off-route never blocks the rest of the
 *  flow (see the note in `use-resume-upload-ftue`). */
export const onPortalPath = (path: string): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith(path)

/**
 * Stamp a step with the chrome every HR-portal coachmark shares: it lives on the
 * one `portal-hr-activation` flow, renders as the teal "Pelgo" coachmark variant,
 * blocks unrelated page actions, and a skip/ESC retires only that step (each step is its
 * own phase). Callers pass everything else (`id`, `phase`, `order`, `trigger`,
 * `condition`, `anchorId`, `content`, `placement`, and any opt-ins). Any of the
 * stamped fields can still be overridden per step (e.g. `welcome` sets
 * `skipScope: 'flow'`).
 */
export function portalStep(
  step: Omit<FtueStep, 'flow' | 'presentation' | 'variant' | 'modal'>,
): FtueStep {
  return {
    flow: PORTAL_HR_ACTIVATION_FLOW,
    presentation: 'coachmark',
    variant: 'portal',
    modal: true,
    skipScope: 'phase',
    ...step,
  }
}
